import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Input, Collapse, Pagination, Checkbox, Button, message, Modal, Form, Tooltip } from 'antd';
import {
	PlusCircleOutlined,
	SearchOutlined,
	DeleteOutlined,
	InfoOutlined,
	InfoCircleFilled,
	AimOutlined,
} from '@ant-design/icons';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';
import SelectWithAction, {
	SelectWithActionOption,
} from '../../components/General/SelectWithAction';
import InfrastructureService from '../../domain/Infrastructure/InfraestructureService';
import type { PaginationProps } from 'antd';
import Icon from '@ant-design/icons';
import './index.scss';
import DashboardContext from '@/contexts/DashboardContext';
import ViewGraphicSvg from '../../assets/svg/ViewGraphic';
import HideViewGraphicSvg from '../../assets/svg/HideViewGraphic';
import { useNavigate } from 'react-router-dom';
import {
	addResumeAndReadingToListThunk,
	deleteResumeAndAllChildReadingsToList,
	restartResumeAndReadingList,
	getAllListThunk,
} from '@/redux/slices/readingsResumesSlice';
import { useDispatch } from 'react-redux';
import SelectTypeInfrastructure from '@/basicsComponents/Selects/SelectTypeInfrastructure';

import { Search } from '@hydrodigital/design-system';

const { Panel } = Collapse;
const infrastructureService = new InfrastructureService();

const InfrastructureDashboardView: FC = () => {
	const { value: context, setValue: setContext } = useContext(DashboardContext);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const mountedRef = useRef(false);
	const previousLocationRef = useRef(context.selectedLocation);
	const [name, setName] = useState<any>('');
	const [parameters, setParameters] = useState([]);
	const [favoriteGroups, setFavoriteGroups] = useState<SelectWithActionOption[]>([]);
	const [selectedGroup, setSelectedGroup] = useState<string | undefined>(undefined);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [form] = Form.useForm();
	const [activeKeys, setActiveKeys] = useState<string[]>(
		context.infraToShow?.id ? [context.infraToShow.id] : [],
	);
	const [selectedParameters, setSelectedParameters] = useState<any>([]);
	const [selectedActiveKeys, setSelectedActiveKeys] = useState<string[]>([]);
	const [pagination, setPagination] = useState({
		page: 1,
		pageSize: 10,
		totalRecords: 0,
	});

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedGroupToDelete, setSelectedGroupToDelete] = useState<number | undefined>(undefined);

	const [temporalName, setTemporalName] = useState('');
	const selectTypeInfrastructureRef = useRef<any>(null);
	const [selectedInfraType, setselectedInfraType] = useState<any>(null);
	const [loadingParameters, setLoadingParameters] = useState(false);
	const [isLocationChanging, setIsLocationChanging] = useState(false);

	const handleCollapseChange = (keys: string[] | string) => {
		const newKeys = Array.isArray(keys) ? keys : [keys];
		setActiveKeys(newKeys);
	};

	const handleSelectedCollapseChange = (keys: string[] | string) => {
		const newKeys = Array.isArray(keys) ? keys : [keys];
		setSelectedActiveKeys(newKeys);
	};

	const genExtra = () => (
		<PlusCircleOutlined
			style={{ color: '#fff' }}
			onClick={(event) => event.stopPropagation()}
			rev={undefined}
		/>
	);

	const isParameterChecked = (parameter) => {
		// Verificar si el parámetro está en los sensores seleccionados del contexto actual
		return !!context.selectedSensors.find((sensor: any) => {
			// Añadir verificación de infraestructura para evitar conflictos entre ubicaciones
			const sameParameter = sensor.measurementTargetId === parameter.paremeterId;
			const sameLocation = context.selectedLocation === sensor.locationId;
			return sameParameter && sameLocation;
		});
	};

	const handleUpdateParameters = async (
		name: string | null,
		page: number,
		size: number,
		infraTypeId: number | null,
		forceUnchecked: boolean = false,
	) => {
		const locationId = context.selectedLocation;

		// Prevent multiple simultaneous calls or calls during location changes
		if (loadingParameters || isLocationChanging) {
			return;
		}

		try {
			if (context.selectedLocation) {
				setLoadingParameters(true);
				const infra: any = await infrastructureService.getInfraestructureInUse(
					context.selectedLocation,
					page,
					size,
					name,
					null,
					infraTypeId,
					true,
				);

				// Only update if we're still on the same location (prevent race conditions)
				if (context.selectedLocation === locationId) {
					setPagination((prevPagination) => ({
						...prevPagination,
						totalRecords: infra.totalRecords,
						page: infra.currentPage,
					}));
					setParameters(
						infra.data.map((infra: any) => ({
							...infra,
							measurementParameters: infra.measurementParameters.map((measurement: any) => ({
								...measurement,
								// Force parameters unchecked when location changes
								checked: forceUnchecked ? false : isParameterChecked(measurement),
							})),
						})),
					);
				}
			}
		} catch (error) {
			console.error('Error updating parameters:', error);
		} finally {
			setLoadingParameters(false);
			// Only reset location changing flag if this was a location-triggered call (name === null)
			if (name === null) {
				setTimeout(() => {
					setIsLocationChanging(false);
				}, 500); // Longer delay to ensure all cascading events are processed
			}
		}
	};

	const handleUpdateFavoriteGroups = async () => {
		const locationId = context.selectedLocation;
		if (locationId) {
			const infra: any = await infrastructureService.getFavoriteGroups(locationId);

			setFavoriteGroups(
				infra.map((infra: any) => ({
					label: infra.groupName,
					value: infra.groupId,
					data: infra.items,
				})),
			);
		}
	};

	useEffect(() => {
		setFavoriteGroups((_) => []);
		handleUpdateFavoriteGroups();
	}, [context.selectedLocation]);

	useEffect(() => {
		if (context.selectedSensors.length === 0) {
			// No sensors selected — clear selectedParameters
			setSelectedParameters([]);
			return;
		}

		setSelectedParameters((prev: any[]) => {
			if (prev.length > 0) {
				// Normal flow: filter out deselected infrastructures
				return prev.filter((parameter: any) => {
					return context.selectedSensors.some((sensor) => {
						return sensor.infrastructureId === parameter.infraestructureId;
					});
				});
			}

			// Rebuild from loaded parameters (remount after tab switch)
			if (parameters.length > 0) {
				const selectedInfraIds = new Set(
					context.selectedSensors.map((sensor: any) => sensor.infrastructureId),
				);
				const rebuilt = parameters.filter((param: any) => selectedInfraIds.has(param.infraestructureId));
				return rebuilt;
			}

			return prev;
		});
	}, [context.selectedSensors, parameters]);

	const onChangePagination: PaginationProps['onChange'] = (page) => {
		if (!loadingParameters && !isLocationChanging) {
			const currentPageSize = pagination.pageSize;
			setPagination({ ...pagination, page });
			void handleUpdateParameters(name, page, currentPageSize, selectedInfraType);
		}
	};

	const renderParameterPanel = (parameter: any, isAlreadySelected: boolean = false) => {
		const handleCheckboxChange = (target: any, checked: boolean) => {
			if (!isAlreadySelected) {
				setActiveKeys((prev: string[]) => {
					return prev.filter((item) => item != parameter?.infraestructureId);
				});
				setSelectedActiveKeys((prev: string[]) => {
					return prev.filter((item) => item != parameter?.infraestructureId);
				});
			}

			if (checked) {
				setSelectedParameters((prev: any[]) => {
					if (prev.includes(parameter)) return prev;
					return prev.concat(parameter);
				});

				const [dateFrom, dataTo] = context.selectedDate;
				dispatch(
					addResumeAndReadingToListThunk(
						parameter?.infraestructureId,
						parameter?.infraTypeId,
						target.paremeterId,
						target.serialNumberSource,
						dateFrom,
						dataTo,
					) as any,
				);
				// ✅ Use functional update to avoid stale context closure
				setContext((prevContext) => ({
					...prevContext,
					isExpandedParamsCard: false,
					showParamsCard: true,
					selectedSensors: [
						...prevContext.selectedSensors,
						{
							measurementTargetId: target.paremeterId,
							measurementTargetName: target.paremeterName,
							serialNumberMeasurementTool: null,
							infrastructureId: parameter?.infraestructureId,
							infrastructureTypeId: parameter?.infraTypeId,
							sourceId: null,
							locationId: prevContext.selectedLocation,
						},
					],
				}));
			} else {
				dispatch(deleteResumeAndAllChildReadingsToList(target.paremeterId) as any);
				// ✅ Use functional update to avoid stale context closure
				setContext((prevContext) => {
					const newSensors = prevContext.selectedSensors.filter(
						(sensor) => sensor.measurementTargetId !== target.paremeterId,
					);
					setSelectedParameters((prev: any) => {
						if (newSensors.some((sensor) => sensor.infrastructureId == parameter.infraestructureId))
							return prev;

						return prev.filter(
							(item: any) => item.infraestructureId !== parameter.infraestructureId,
						);
					});
					return {
						...prevContext,
						selectedSensors: newSensors,
					};
				});
			}
		};

		const handleZoomToAsset = (event: React.MouseEvent) => {
			event.stopPropagation();
			dashboardMapEventsService.setSubject({
				type: 'zoomToAsset',
				data: { infrastructureId: parameter?.infraestructureId },
			});
		};

		return (
			<Panel
				header={
					<div className="flex items-center justify-between w-full">
						<div className="flex column">
							<span>{parameter?.infraestructureName}</span>
							<span style={{ color: '#74788D', fontSize: '10px' }}>
								{parameter.infrastructureTypeName}
							</span>
						</div>
						<Tooltip title="Zoom to asset on map">
							<AimOutlined
								className="cursor-pointer"
								style={{ color: '#04628C', fontSize: '16px', marginRight: '8px' }}
								onClick={handleZoomToAsset}
							/>
						</Tooltip>
					</div>
				}
				key={parameter?.infraestructureId}
				extra={genExtra()}
			>
				<div className="flex column columnapequena">
					{parameter?.measurementParameters?.map((target: any, i: number) => (
						<Checkbox
							key={`checkbox_${i}`}
							checked={context.selectedSensors.some(
								(sensor: any) => sensor.measurementTargetId === target.paremeterId,
							)}
							onChange={(e) => handleCheckboxChange(target, e.target.checked)}
						>
							<span className="parameter-name">{target.paremeterName}</span>
						</Checkbox>
					))}
				</div>
			</Panel>
		);
	};

	function filteredArray(baseArray, filterArray) {
		const ids = new Set(filterArray.map((item) => item.infraestructureId));
		return baseArray.filter((item) => !ids.has(item.infraestructureId));
	}

	useEffect(() => {
		const resetState = async () => {
			if (!mountedRef.current) {
				// First mount: mark as mounted and load parameters without clearing readings
				mountedRef.current = true;
				previousLocationRef.current = context.selectedLocation;
				if (context.selectedLocation && !context.infraToShow.name) {
					handleUpdateParameters(null, 1, 10, selectedInfraType);
				}
				return;
			}

			// Check if the location actually changed
			const locationActuallyChanged = previousLocationRef.current !== context.selectedLocation;
			previousLocationRef.current = context.selectedLocation;

			if (!locationActuallyChanged) {
				// Remount from tab switch — do NOT clear readings, just reload parameter list
				if (context.selectedLocation && !context.infraToShow.name) {
					handleUpdateParameters(null, 1, 10, selectedInfraType);
				}
				return;
			}

			// Set location changing flag to prevent other calls
			setIsLocationChanging(true);

			// Clear all states immediately — only when location ACTUALLY changes
			dispatch(restartResumeAndReadingList() as any);
			setParameters([]);
			setName('');
			setTemporalName('');
			setPagination({
				page: 1,
				pageSize: 10,
				totalRecords: 0,
			});
			setSelectedParameters([]);
			setActiveKeys([]);
			setSelectedActiveKeys([]);
			setSelectedGroup(undefined);

			// Only load parameters if there's a selected location and no infraToShow name
			if (context.selectedLocation && !context.infraToShow.name) {
				setTimeout(() => {
					// Don't reset isLocationChanging here - let the API call completion handle it
					handleUpdateParameters(null, 1, 10, selectedInfraType);
				}, 300);
			} else {
				setIsLocationChanging(false); // Reset flag even when skipping
			}
		};

		resetState();
	}, [context.selectedLocation]);

	useEffect(() => {
		if (context.infraToShow.name) {
			setPagination({ ...pagination, page: 1 });
			setName(context.infraToShow.name);
			setTemporalName(context.infraToShow.name);
			handleUpdateParameters(context.infraToShow.name, 1, 10, selectedInfraType);
		}
	}, [context.infraToShow.name]);

	// Handler for deleting a favorite group
	const handleDeleteFavoriteGroup = async (value: string | number) => {
		try {
			await infrastructureService.deleteFavoriteGroup(context.selectedLocation, Number(value));

			// Update local state
			setFavoriteGroups((prev) => prev.filter((group) => group.value !== value));

			// Clear selected group if it was the deleted one
			if (selectedGroup === value) {
				setSelectedGroup(undefined);
			}

			message.success(`Group deleted successfully`);
			setSelectedParameters([]);
			dispatch(restartResumeAndReadingList() as any);
			setContext((prevContext) => ({
				...prevContext,
				selectedSensors: [],
			}));
			setIsDeleteModalOpen(false);
		} catch (error) {
			console.error('Error deleting favorite group:', error);
			message.error('Failed to delete group');
		}
	};

	const showDeleteModal = (value: string | number) => {
		setSelectedGroupToDelete(Number(value));
		setIsDeleteModalOpen(true);
	};

	// Handler for selecting a favorite group
	const handleSelectGroup = async (value: string) => {
		setSelectedGroup(value);

		// Clear existing selections when selecting a new group
		setSelectedParameters([]);

		// Clear Redux state
		dispatch(restartResumeAndReadingList() as any);

		// Find the selected group data
		const selectedGroupData = favoriteGroups.find((group) => group.value === Number(value));

		if (selectedGroupData?.data) {
			// Process each item in the group by fetching individual infrastructures
			const parametersToAdd: any[] = [];

			// Create a map to group parameters by infrastructure
			const infrastructureMap = new Map<number, number[]>();
			selectedGroupData.data.forEach((item: { infrastructureId: number; parameterId: number }) => {
				if (!infrastructureMap.has(item.infrastructureId)) {
					infrastructureMap.set(item.infrastructureId, []);
				}
				infrastructureMap.get(item.infrastructureId)!.push(item.parameterId);
			});

			// Fetch each infrastructure individually
			const fetchPromises = Array.from(infrastructureMap.entries()).map(
				async ([infrastructureId, parameterIds]) => {
					try {
						// Use getInfraestructureInUse to get infrastructure with all its parameters
						const response = await infrastructureService.getInfraestructureInUse(
							context.selectedLocation,
							1,
							1000, // Large page size to get all parameters
							null,
							infrastructureId,
							null,
							true,
						);

						// Find the specific infrastructure in the response
						const infrastructureData = response.data.find(
							(infra: any) => infra.infraestructureId === infrastructureId,
						);

						if (infrastructureData?.measurementParameters) {
							// Return the infrastructure with ALL its parameters
							// The selection state will be handled by the context.selectedSensors
							return {
								...infrastructureData,
								measurementParameters: infrastructureData.measurementParameters,
							};
						}
					} catch (error) {}
					return null;
				},
			);

			// Wait for all fetches to complete
			const fetchedInfrastructures = await Promise.all(fetchPromises);

			// Filter out null results and add to parametersToAdd
			fetchedInfrastructures.forEach((infrastructure) => {
				if (infrastructure) {
					parametersToAdd.push(infrastructure);
				}
			});

			// Add the found parameters to selectedParameters and execute dispatches
			if (parametersToAdd.length > 0) {
				// Collect only the sensors that are in the favorite group
				const allSensors = parametersToAdd.flatMap((infrastructure: any) => {
					// Get the parameter IDs that should be selected for this infrastructure
					const parameterIdsForThisInfra =
						infrastructureMap.get(infrastructure.infraestructureId) || [];

					// Filter only the parameters that are in the favorite group
					const selectedParameters =
						infrastructure.measurementParameters?.filter((parameter: any) =>
							parameterIdsForThisInfra.includes(parameter.paremeterId),
						) || [];

					return selectedParameters.map((parameter: any) => ({
						measurementTargetId: parameter.paremeterId,
						measurementTargetName: parameter.paremeterName,
						serialNumberMeasurementTool: null,
						infrastructureId: infrastructure.infraestructureId,
						infrastructureTypeId: infrastructure.infraTypeId,
						sourceId: null,
						locationId: context.selectedLocation,
					}));
				});

				const totalParametersAdded = allSensors.length;

				// Update all states together to avoid race conditions
				setSelectedParameters(parametersToAdd);
				setContext((prevContext: any) => ({
					...prevContext,
					selectedSensors: allSensors,
				}));

				// Dispatch getAllListThunk to load data for the graph
				const [dateFrom, dataTo] = context.selectedDate;
				dispatch(getAllListThunk(allSensors, dateFrom, dataTo) as any);

				// Update the main parameters list to show these infrastructures
				// This ensures the infrastructures appear in the main list with proper checked/unchecked state
				handleUpdateParameters(name, pagination.page, pagination.pageSize, selectedInfraType);

				message.success(
					`Group "${selectedGroupData.label}" loaded with ${totalParametersAdded} parameters`,
				);
			} else {
				message.info(`No new parameters found in group "${selectedGroupData.label}"`);
			}
		} else {
			setContext((prevContext: any) => ({
				...prevContext,
				isExpandedParamsCard: false,
			}));
		}
	};

	// Handler for opening the create group modal
	const handleOpenModal = () => {
		if (selectedParameters.length === 0) {
			message.warning('Please select parameters first to create a group');
			return;
		}
		setIsModalOpen(true);
	};

	// Handler for creating a new group
	const handleCreateGroup = async (values: any) => {
		try {
			const newGroup = {
				groupName: values.groupName,
				items: selectedParameters
					.map((parameter: any) => {
						// Filter only the parameters that are actually selected (checked) by the user
						const selectedMeasurements = parameter.measurementParameters.filter(
							(measurement: any) => {
								return context.selectedSensors.some(
									(sensor: any) =>
										sensor.measurementTargetId === measurement.paremeterId &&
										sensor.infrastructureId === parameter.infraestructureId,
								);
							},
						);

						return selectedMeasurements.map((measurement: any) => {
							return {
								infrastructureId: parameter.infraestructureId,
								parameterId: measurement.paremeterId,
							};
						});
					})
					.flat(),
			};
			await infrastructureService.postFavoriteGroup(context.selectedLocation, newGroup);
			message.success(`Group "${values.groupName}" created successfully`);
		} catch (error) {
			message.error('Failed to create group');
		} finally {
			setIsModalOpen(false);
			form.resetFields();
			handleUpdateFavoriteGroups();
		}
	};

	// Handler for canceling modal
	const handleCancelModal = () => {
		setIsModalOpen(false);
		form.resetFields();
	};

	const handleClearGroup = () => {
		setSelectedGroup(undefined);
		setSelectedParameters([]);
		dispatch(restartResumeAndReadingList() as any);
		setContext((prevContext) => ({
			...prevContext,
			selectedSensors: [],
		}));
	};

	return (
		<div style={{ minHeight: 540 }}>
			<div className="flex flex-column justify-between ml-2 mr-2 pb-1 ">
				<div className="flex flex-column mb-2">
					<span className="mb-1 font-bold">Favorites Groups</span>
					<div>
						{favoriteGroups.length > 0 ? (
							<SelectWithAction
								placeholder="Select a group"
								options={favoriteGroups}
								onAction={showDeleteModal}
								tooltip="Delete group"
								value={selectedGroup}
								onChange={handleSelectGroup}
								allowClear
								onClear={handleClearGroup}
							/>
						) : (
							<span className="text-gray-500 text-xs italic">
								<span>No favorite groups found. Select some parameters to create one.</span>
								<Tooltip
									title={`First, select
								assets with their parameters and then click the "plus" button to add a new
								group to your list.`}
								>
									<InfoCircleFilled className="ml-1" />
								</Tooltip>
							</span>
						)}
					</div>
				</div>

				<div className="flex flex-column mb-2">
					<span className="mb-1 font-bold">Filters</span>
				</div>

				<div style={{ width: 200, minWidth: 200 }}>
					<Search
						placeholder="Search Name"
						size="small"
						style={{ width: '100%' }}
						value={temporalName}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setTemporalName(e.target.value);
							if (e.target.value === '' && !loadingParameters && !isLocationChanging) {
								// ✅ Use functional update to avoid stale context closure
								setContext((prevContext) => ({
									...prevContext,
									infraToShow: { ...prevContext.infraToShow, name: null },
								}));
								setName('');
								setPagination({ ...pagination, page: 1 });
								handleUpdateParameters(null, 1, pagination.pageSize, selectedInfraType);
							}
						}}
						onKeyDown={(e) => {
							if (e.target) {
								if (e.key === 'Enter') {
									if (!loadingParameters && !isLocationChanging) {
										setName(e.currentTarget.value);
										setPagination({ ...pagination, page: 1 });
										handleUpdateParameters(
											e.currentTarget.value,
											1,
											pagination.pageSize,
											selectedInfraType,
										);
									}
								}
							}
						}}
						onClear={() => {
							setName(null);
						}}
						showClearButton
					/>
				</div>
			</div>
			<div className="flex justify-between ml-2 mr-2">
				<div style={{ width: 160, marginRight: 10 }}>
					<SelectTypeInfrastructure
						ref={selectTypeInfrastructureRef}
						onSelect={(value: any) => {
							if (!loadingParameters && !isLocationChanging) {
								setselectedInfraType(value);
								setPagination({ ...pagination, page: 1 });
								const infraName = name && name.trim() !== '' && name.length > 0 ? name : null;
								handleUpdateParameters(infraName, 1, pagination.pageSize, value);
							}
						}}
					/>
				</div>
				<Icon
					component={context.showParamsCard ? ViewGraphicSvg : HideViewGraphicSvg}
					className="cursor-pointer"
					onClick={() =>
						// ✅ Use functional update to avoid stale context closure
						setContext((prevContext) => ({
							...prevContext,
							showParamsCard: !prevContext.showParamsCard,
						}))
					}
				/>
			</div>

			<div className="mt-3 ml-2 mr-2">
				{selectedParameters.length > 0 && (
					<div className="mb-2">
						<div className="flex items-center gap-2 mb-1">
							<span className="font-bold">Selected Parameters</span>
							<Tooltip title="Create favorite group from selected parameters">
								<PlusCircleOutlined
									style={{ color: '#04628C', cursor: 'pointer', fontSize: '16px' }}
									onClick={handleOpenModal}
								/>
							</Tooltip>
						</div>
						<Collapse
							activeKey={selectedActiveKeys}
							onChange={handleSelectedCollapseChange}
							expandIconPosition={'start'}
							style={{ overflow: 'auto' }}
						>
							{selectedParameters.map((parameter: any) => renderParameterPanel(parameter, true))}
						</Collapse>
					</div>
				)}
				<div>
					{selectedParameters.length > 0 && <span className="mb-1 font-bold">Parameters</span>}
					{parameters.length > 0 && !loadingParameters ? (
						<>
							{' '}
							<Collapse
								activeKey={activeKeys}
								onChange={handleCollapseChange}
								expandIconPosition={'start'}
								style={{ overflow: 'auto' }}
							>
								{filteredArray(parameters, selectedParameters).map((parameter: any) =>
									renderParameterPanel(parameter),
								)}
							</Collapse>
							<Pagination
								style={{ marginTop: '2rem', width: '240px', marginLeft: '-15px', justifyContent: 'center' }}
								onChange={onChangePagination}
								current={pagination.page}
								pageSize={pagination.pageSize}
								total={pagination.totalRecords}
								showSizeChanger={false}
								showLessItems
								itemRender={(page, type, originalElement) => {
									if (type === 'page') {
										if (Math.abs(page - pagination.page) <= 3) {
											return originalElement;
										}
										return null;
									}
									return originalElement;
								}}
							/>
						</>
					) : parameters.length === 0 ? (
						<span className="text-gray-500 text-xs italic">There are no assets of this type.</span>
					) : (
						<span className="text-gray-500 text-xs italic">...Loading Parameters for location</span>
					)}
				</div>
			</div>

			{/* Create Group Modal */}
			<Modal
				title="Create New Group"
				open={isModalOpen}
				onCancel={handleCancelModal}
				footer={null}
				width={500}
			>
				<Form form={form} layout="vertical" onFinish={handleCreateGroup}>
					<Form.Item
						label="Group Name"
						name="groupName"
						rules={[
							{ required: true, message: 'Please enter a group name' },
							{ min: 2, message: 'Group name must be at least 2 characters' },
						]}
					>
						<Input placeholder="Enter group name" />
					</Form.Item>

					<Form.Item
						label={`Selected Parameters (${selectedParameters.reduce((total, param) => {
							const selectedCount =
								param.measurementParameters?.filter((measurement: any) => {
									return context.selectedSensors.some(
										(sensor: any) =>
											sensor.measurementTargetId === measurement.paremeterId &&
											sensor.infrastructureId === param.infraestructureId,
									);
								}).length || 0;
							return total + selectedCount;
						}, 0)})`}
					>
						<div
							style={{
								maxHeight: '200px',
								overflow: 'auto',
								border: '1px solid #d9d9d9',
								borderRadius: '6px',
								padding: '8px',
							}}
						>
							{selectedParameters.map((param: any, index: number) => {
								// Filter only the parameters that are actually selected (checked) by the user
								const selectedMeasurements =
									param.measurementParameters?.filter((measurement: any) => {
										return context.selectedSensors.some(
											(sensor: any) =>
												sensor.measurementTargetId === measurement.paremeterId &&
												sensor.infrastructureId === param.infraestructureId,
										);
									}) || [];

								// Only show infrastructure if it has selected parameters
								if (selectedMeasurements.length === 0) return null;

								return (
									<div
										key={index}
										className="flex flex-column"
										style={{
											padding: '4px 0',
											borderBottom:
												index < selectedParameters.length - 1 ? '1px solid #f0f0f0' : 'none',
										}}
									>
										<strong>{param.infraestructureName}</strong>

										<div style={{ fontSize: '12px', color: '#666' }}>
											{selectedMeasurements.map((mp: any, mpIndex: number) => (
												<span key={mpIndex}>
													{mp.paremeterName}
													{mpIndex < selectedMeasurements.length - 1 ? ', ' : ''}
												</span>
											))}
										</div>
										<span style={{ fontSize: '11px', color: '#999' }}>
											{param.infrastructureTypeName}
										</span>
									</div>
								);
							})}
						</div>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
						<Button onClick={handleCancelModal} style={{ marginRight: 8 }}>
							Cancel
						</Button>
						<Button type="primary" htmlType="submit">
							Create Group
						</Button>
					</Form.Item>
				</Form>
			</Modal>
			<Modal
				title="Delete Favorite Group"
				open={isDeleteModalOpen}
				onOk={() => handleDeleteFavoriteGroup(Number(selectedGroupToDelete))}
				onCancel={() => {
					setIsDeleteModalOpen(false);
					setSelectedGroupToDelete(undefined);
				}}
			>
				Are you sure you want to delete this favorite group?
			</Modal>
		</div>
	);
};

export default InfrastructureDashboardView;
